import { defineMessages } from 'react-intl';
import { all, takeEvery, takeLatest, call, put, delay, select } from 'redux-saga/effects';
import { fetchEntity } from '@classpass/saga-tools';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { reloadPage } from 'helpers/page-utils';
import {
  updateLocale as updateLocaleEntity,
  updatePassword as updatePasswordEntity,
  brazeData,
} from 'store/saga/entities/bound';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { reportError } from 'helpers/error-logging';
import { selectActiveVenueId } from 'selectors/general';

export const messages = defineMessages({
  successUpdatePW: {
    id: 'settings.saga.success-update-pw',
    defaultMessage: 'You updated your password',
  },
  errorUpdatePW: {
    id: 'settings.saga.error-update-pw',
    defaultMessage: 'We were unable to update your password at this time',
  },
  successUpdateLocale: {
    id: 'settings.saga.success-update-locale',
    defaultMessage: 'Your language has been updated',
  },
  errorUpdateLocale: {
    id: 'settings.saga.error-update-locale',
    defaultMessage: 'We were unable to update your language at this time',
  },
});

export const UPDATE_LOCALE = 'venuePortal/settingsAccount/UPDATE_LOCALE';
export const updateLocale = locale => ({ type: UPDATE_LOCALE, locale });

export const UPDATE_PASSWORD = 'venuePortal/settingsAccount/UPDATE_PASSWORD';
export const updatePassword = (newPassword, formAsyncFns) => ({ type: UPDATE_PASSWORD, newPassword, formAsyncFns });

export const RELOAD_DELAY_LENGTH = 2000;

export const venueSelection = venueId => ({ selectedVenueId: venueId });

export function* doUpdateLocale(dependencies, action) {
  try {
    const { locale } = action;
    yield call(fetchEntity, updateLocaleEntity, [dependencies, locale]);
    yield put(createInfoBanner(messages.successUpdateLocale));
    yield delay(RELOAD_DELAY_LENGTH);
    yield call(reloadPage, true);
  } catch (error) {
    yield all([put(createErrorBanner(messages.errorUpdateLocale)), call(reportError, error)]);
  }
}

export function* doUpdatePassword(dependencies, action) {
  const { newPassword, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit, resetForm } = formAsyncFns;

  try {
    yield call(fetchEntity, updatePasswordEntity, [dependencies, newPassword]);
    yield call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.PASSWORD_CHANGED);
    yield all([call(resolveSubmit), put(createInfoBanner(messages.successUpdatePW))]);
    // resolveSubmit resets the form to the values just submitted, which would leave the
    // new password sitting in both fields.
    yield call(resetForm, { password: '', repeatPassword: '' });
  } catch (error) {
    yield all([call(rejectSubmit), put(createErrorBanner(messages.errorUpdatePW)), call(reportError, error)]);
  }
}

export function* handleProfileAccountSettingsEnter(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    if (!venueId) return;
    yield call(fetchEntity, brazeData, [client, venueId]);
  } catch (error) {
    yield call(reportError, error);
  }
}

export function* watchActions(dependencies) {
  yield takeLatest(UPDATE_LOCALE, doUpdateLocale, dependencies);
  yield takeEvery(UPDATE_PASSWORD, doUpdatePassword, dependencies);
}
